import { Vault } from '@app/hooks/plugins/useVaults';
import {
  Currency,
  CurrencyAmount,
  Field,
  Pool,
  tryParseAmount
} from '@cryptoalgebra/kim-sdk';
import { Address } from '@thirdweb-dev/sdk';
import { useRouter } from 'next/router';
import { useConfig } from '@app/config';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { useGammaVaults } from '@app/hooks/gamma/useGammaVaults';
import { useEffect, useMemo, useRef } from 'react';
import { useBalance, useContract } from '@thirdweb-dev/react';
import { useAsync, useAsyncFn } from 'react-use';
import { EMPTY_ADDRESS } from '@app/constants/common';
import { useIsNitroPool } from '@app/hooks/nitro/useIsNitroPool';
import uniProxyAbi from 'abis/gamma/uniProxy.json';
import { parseUnits } from '@app/helpers/format';
import { usePool } from '@app/hooks/v3/usePool';
import { formatUnits } from 'ethers/lib/utils';
import { BigNumber } from 'ethers';

export interface IDerivedLiquidityByGammaInfo {
  pool: Pool | null;
  vault: Vault | null;
  // amount: string;
  nftPoolAddress: string | null | undefined;
  getPoolAddress: () => Promise<string | null | undefined>;
  isSwappedPair: boolean;
  lockDuration: number;
  isNitroPool: boolean;
  token0: Currency | undefined;
  token1: Currency | undefined;
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> };
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> };
  independentField: Field;
  dependentField: Field;
  errorMessage: string | undefined;
  pairVaults: Vault[];
}

export function useDerivedLiquidityByGammaInfo(
  poolAddress: Address | undefined
): IDerivedLiquidityByGammaInfo {
  const { query } = useRouter();
  const config = useConfig();

  const {
    currencyA,
    currencyB,
    strategy,
    typedValue,
    independentField,
    mode,
    actions: { setStrategy, setMode }
  } = useLiquidityWizardState();

  // const {
  //   vaultId,
  //   amount,
  //   // lockDuration,
  //   actions: { setVaultId }
  // } = useLiquidityByGammaState();

  const { data: vaults } = useGammaVaults();

  const pairVaults = useMemo(() => {
    if (!vaults) {
      return [];
    }

    return vaults.filter(
      v =>
        (v.token0.id.toLowerCase() ===
          currencyA?.wrapped.address.toLowerCase() &&
          v.token1.id.toLowerCase() ===
            currencyB?.wrapped.address.toLowerCase()) ||
        (v.token0.id.toLowerCase() ===
          currencyB?.wrapped.address.toLowerCase() &&
          v.token1.id.toLowerCase() ===
            currencyA?.wrapped.address.toLowerCase())
    );
  }, [vaults, currencyA, currencyB]);

  const selectedVault = useMemo(() => {
    if (!vaults) {
      return;
    }

    return vaults?.find(
      item => item.id?.toLowerCase() === strategy?.toLowerCase()
    );
  }, [strategy, vaults]);

  const { contract: poolFactoryContract } = useContract(
    config?.CONTRACTS.NFT_POOL_FACTORY
  );

  const [{ value: nftPoolAddress }, getPoolAddress] = useAsyncFn(async () => {
    if (!poolFactoryContract || !strategy) {
      return;
    }

    const nftPoolAddress = await poolFactoryContract.call('getPool', [
      strategy
    ]);

    return nftPoolAddress === EMPTY_ADDRESS ? null : nftPoolAddress;
  }, [poolFactoryContract, strategy]);

  const { isNitroPool } = useIsNitroPool(nftPoolAddress);

  useEffect(() => {
    getPoolAddress();
  }, [getPoolAddress]);

  const vaultSetRef = useRef(false);

  useEffect(() => {
    if (query && query.vaultId && !vaultSetRef.current) {
      const { vaultId } = query;

      vaultSetRef.current = true;

      setMode('auto');
      setStrategy(vaultId as string);
    }
  }, [query.vaultId, query, setStrategy, setMode]);

  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB
    }),
    [currencyA, currencyB]
  );

  // formatted with tokens
  const [token0, token1] = useMemo(
    () => [currencyA, currencyB, currencyA],
    [currencyA, currencyB]
  );

  const { data: token0Balance } = useBalance(
    token0?.isNative ? '' : token0?.wrapped.address
  );
  const { data: token1Balance } = useBalance(
    token1?.isNative ? '' : token1?.wrapped.address
  );

  const independentAmount: CurrencyAmount<Currency> | undefined =
    tryParseAmount(typedValue, currencies[independentField]);

  const dependentField =
    independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A;

  const { contract: uniProxyContract } = useContract(
    config?.GAMMA.UNI_PROXY,
    uniProxyAbi
  );

  const { value: dependentAmount } = useAsync(async () => {
    if (
      !uniProxyContract ||
      !currencies ||
      !currencies[independentField] ||
      !selectedVault ||
      !typedValue
    ) {
      return;
    }

    try {
      const amounts = await uniProxyContract.call('getDepositAmount', [
        selectedVault.id,
        currencies[independentField]!.wrapped.address,
        parseUnits(typedValue, currencies[independentField]!.wrapped.decimals)
      ]);

      const avg: BigNumber = amounts.amountStart.add(amounts.amountEnd).div(2);

      return currencies[dependentField] !== undefined
        ? CurrencyAmount.fromRawAmount(
            currencies[dependentField]!,
            avg.toString()
          )
        : undefined;
    } catch (e) {
      console.error(e);
    }
  }, [
    selectedVault,
    uniProxyContract,
    currencies,
    independentField,
    typedValue,
    dependentField
  ]);

  const parsedAmounts: {
    [field in Field]: CurrencyAmount<Currency> | undefined;
  } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A
          ? independentAmount
          : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A
          ? dependentAmount
          : independentAmount
    };
  }, [dependentAmount, independentAmount, independentField]);

  const [, pool] = usePool(poolAddress as Address);

  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]:
      token0 && token0Balance
        ? CurrencyAmount.fromRawAmount(token0, token0Balance.value.toString())
        : undefined,
    [Field.CURRENCY_B]:
      token1 && token1Balance
        ? CurrencyAmount.fromRawAmount(token1, token1Balance.value.toString())
        : undefined
  };

  const isSwappedPair = useMemo(() => {
    if (!pool) {
      return false;
    }

    return (
      pool.token0.address.toLowerCase() !==
      currencyA?.wrapped.address.toLowerCase()
    );
  }, [pool, currencyA]);

  let errorMessage;

  if (
    parsedAmounts.CURRENCY_A &&
    currencyBalances.CURRENCY_A?.lessThan(parsedAmounts.CURRENCY_A)
  ) {
    errorMessage = `Insufficient ${currencyA?.symbol} balance`;
  }

  if (
    parsedAmounts.CURRENCY_B &&
    currencyBalances.CURRENCY_B?.lessThan(parsedAmounts.CURRENCY_B)
  ) {
    errorMessage = `Insufficient ${currencyB?.symbol} balance`;
  }

  return {
    // amount,
    vault: selectedVault ?? null,
    nftPoolAddress,
    getPoolAddress,
    isNitroPool,
    pairVaults,
    pool,
    token0,
    token1,
    currencyBalances,
    parsedAmounts,
    independentField,
    dependentField,
    isSwappedPair,
    lockDuration: 0,
    errorMessage
  };
}
